/**
 * One protocol opened on Promoted Protocols: its types as a filterable list with the stage each is at, the
 * latest template generation, and where its promotion stands. Read-only: each type links to the protocol's
 * Promote scenarios drawer, where the work is done.
 */

import { useState } from 'react';
import { Link } from 'react-router';
import { AlertTriangle, ShieldCheck } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { useProtocol } from '@/hooks/use-promotion';
import { cn } from '@/lib/utils';
import { counted } from '@/modules/back-testing/format';
import { ReadinessBadge } from '@/modules/back-testing/readiness/readiness-badge';
import { protocolPromoteUrl } from '@/constants/paths';
import { useAllSOPs } from '@/hooks';
import { PromotionCard } from './promotion-card';
import { RollupBar } from './rollup-bar';
import { countOf, isJobActive, jobProgress, STAGE_LABELS, STAGE_ORDER, STAGE_VARIANTS } from './stage';
import type { ProtocolType, TypePromotionStage } from './types';

interface TypeItemProps {
	type: ProtocolType;
	/** Where the type's work is: the protocol's Promote scenarios drawer, opened on this type. */
	href: string | null;
	onOpenBlueprint?: (blueprintId: string) => void;
}

/** What the type needs next, as the link to it says. */
const nextFor = (type: ProtocolType): string => {
	const { verdict, runId, counts } = type.readiness;
	if (verdict === 'ready') return type.stage === 'promoted' ? 'Open' : 'Promote it';
	if (runId && verdict === 'not_ready') return counts?.unjudged ? 'Review replies' : 'Open test run';
	return verdict === 'stale' ? 'Test again' : 'Test it';
};

const FIX_CLASS = 'cursor-pointer font-medium text-indigo-700 hover:text-indigo-900';

const TypeItem = ({ type, href, onOpenBlueprint }: TypeItemProps) => (
	<li className='flex flex-col gap-1.5 border-b border-gray-300 px-4 py-3' data-testid={`type-${type.typeId}`}>
		<div className='flex items-start justify-between gap-3'>
			<p className='min-w-0 flex-1 text-[13px] leading-4.5 font-medium text-text-body'>{type.description}</p>
			<Badge variant={STAGE_VARIANTS[type.stage]} className='text-[10px]'>
				{STAGE_LABELS[type.stage]}
			</Badge>
		</div>
		<div className='flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-gray-800'>
			<ReadinessBadge readiness={type.readiness} className='text-[10px]' />
			{href && (
				<Link to={href} className={FIX_CLASS}>
					{nextFor(type)} →
				</Link>
			)}
			{type.templates.map((template) => {
				const label = `${template.intent} · template ${template.index + 1} ${template.enabled ? '(serving)' : '(draft)'}`;
				return onOpenBlueprint ? (
					<button
						key={`${template.blueprintId}:${template.index}`}
						type='button'
						className='cursor-pointer underline-offset-2 hover:underline'
						onClick={() => onOpenBlueprint(template.blueprintId)}
					>
						{label}
					</button>
				) : (
					<span key={`${template.blueprintId}:${template.index}`}>{label}</span>
				);
			})}
			{type.writes.length > 0 && (
				<span className='inline-flex items-center gap-1' title='Whether a write asks first is set per scenario in Promote.'>
					<ShieldCheck className='size-3' aria-hidden />
					Writes: {type.writes.join(', ')}
				</span>
			)}
		</div>
	</li>
);

interface ProtocolPanelProps {
	sopId: string;
	onOpenBlueprint?: (blueprintId: string) => void;
}

/**
 * Read-only: where each of a protocol's types stands. Generating, testing and promoting run in the protocol's
 * Promote scenarios drawer, which every link here opens.
 */
export const ProtocolPanel = ({ sopId, onOpenBlueprint }: ProtocolPanelProps) => {
	const { data: protocol, isLoading, isError } = useProtocol(sopId);
	// The way to the work is the protocol the SOP is the text of.
	const { data: sops = [] } = useAllSOPs();
	const protocolId = sops.find((sop) => sop.id === sopId)?.workflowId ?? null;
	const [stageFilter, setStageFilter] = useState<TypePromotionStage | null>(null);

	if (isLoading || !protocol) {
		return isError ? (
			<p className='p-4 text-xs text-destructive'>Could not load this protocol.</p>
		) : (
			<div className='flex flex-col gap-3 p-4'>
				<Skeleton className='h-5 w-48' />
				<Skeleton className='h-24 w-full' />
			</div>
		);
	}

	const job = protocol.latestJob;
	const { rollup } = protocol;
	const shown = stageFilter ? protocol.types.filter((type) => type.stage === stageFilter) : protocol.types;

	return (
		<div className='flex min-h-0 flex-1 flex-col'>
			<div className='flex flex-col gap-3 border-b border-gray-500 px-4 py-3'>
				<div className='min-w-0'>
					<h2 className='truncate text-sm font-semibold capitalize'>{protocol.name}</h2>
					<p className='text-xs text-gray-800'>{counted(rollup.types, 'scenario type')}</p>
				</div>
				{protocolId && (
					<p className='text-xs text-gray-800'>
						Scenario types are generated, tested and promoted from the protocol.{' '}
						<Link to={protocolPromoteUrl(protocolId)} className={FIX_CLASS}>
							Open the protocol to promote →
						</Link>
					</p>
				)}
				<PromotionCard sopId={sopId} canEdit={false} />
				<RollupBar rollup={rollup} className='h-2' />
				<div className='flex flex-wrap gap-1.5'>
					{STAGE_ORDER.filter((stage) => countOf(rollup, stage) > 0).map((stage) => (
						<button
							key={stage}
							type='button'
							aria-pressed={stageFilter === stage}
							onClick={() => setStageFilter(stageFilter === stage ? null : stage)}
							className={cn(
								'cursor-pointer rounded-full border px-2 py-0.5 text-[11px] transition-colors',
								stageFilter === stage ? 'border-ink bg-ink text-white' : 'border-gray-400 text-gray-900 hover:bg-gray-100'
							)}
						>
							{STAGE_LABELS[stage]} {countOf(rollup, stage)}
						</button>
					))}
				</div>
				{job && <JobStatus job={job} />}
			</div>

			<ul className='min-h-0 flex-1 overflow-y-auto'>
				{shown.length === 0 ? (
					<li className='px-4 py-3 text-xs text-gray-800'>No scenario types at this stage.</li>
				) : (
					shown.map((type) => (
						<TypeItem
							key={type.typeId}
							type={type}
							href={protocolId ? protocolPromoteUrl(protocolId, type.typeId) : null}
							onOpenBlueprint={onOpenBlueprint}
						/>
					))
				)}
			</ul>

		</div>
	);
};

/** Where the newest generation stands: progress while it runs, the outcome after. */
const JobStatus = ({ job }: { job: NonNullable<ReturnType<typeof useProtocol>['data']>['latestJob'] }) => {
	if (!job) return null;
	if (isJobActive(job)) {
		const { settled, total } = jobProgress(job);
		return (
			<div className='flex flex-col gap-1' role='status'>
				<p className='text-xs text-gray-900'>
					Generating templates: {settled} of {total} types
				</p>
				<Progress value={total ? (settled / total) * 100 : 0} />
			</div>
		);
	}
	const failed = job.types.filter((type) => type.status === 'failed');
	if (job.status === 'failed' || failed.length) {
		return (
			<p className='flex items-start gap-1.5 text-xs text-amber-700' role='status'>
				<AlertTriangle className='mt-0.5 size-3.5 shrink-0' />
				<span>
					{job.status === 'failed' && job.error
						? `The last generation failed: ${job.error}.`
						: `${counted(failed.length, 'type')} could not be generated: ${failed[0]?.error ?? 'unknown error'}.`}
				</span>
			</p>
		);
	}
	return null;
};
